import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  HomeIcon,
  CalendarIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  BellIcon,
  CreditCardIcon,
  UserIcon,
  ClockIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

interface CommandItem {
  id: string;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  action: () => void;
  keywords?: string[];
  category: 'navigation' | 'action' | 'recent';
}

export const CommandPalette = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Define available commands
  const commands: CommandItem[] = [
    // Navigation
    {
      id: 'dashboard',
      title: 'Dashboard',
      subtitle: 'Go to dashboard',
      icon: <HomeIcon className="w-5 h-5" />,
      action: () => navigate('/dashboard'),
      keywords: ['home', 'main', 'overview'],
      category: 'navigation',
    },
    {
      id: 'requests',
      title: 'Requests',
      subtitle: 'View all requests',
      icon: <DocumentTextIcon className="w-5 h-5" />,
      action: () => navigate('/requests'),
      keywords: ['jobs', 'tasks', 'work'],
      category: 'navigation',
    },
    {
      id: 'bookings',
      title: 'Bookings',
      subtitle: 'View all bookings',
      icon: <CalendarIcon className="w-5 h-5" />,
      action: () => navigate('/bookings'),
      keywords: ['appointments', 'schedule'],
      category: 'navigation',
    },
    {
      id: 'calendar',
      title: 'Calendar',
      subtitle: 'View calendar',
      icon: <CalendarIcon className="w-5 h-5" />,
      action: () => navigate('/calendar'),
      keywords: ['schedule', 'dates', 'events'],
      category: 'navigation',
    },
    {
      id: 'contacts',
      title: 'Contacts',
      subtitle: 'Manage contacts',
      icon: <UserGroupIcon className="w-5 h-5" />,
      action: () => navigate('/contacts'),
      keywords: ['customers', 'clients', 'people'],
      category: 'navigation',
    },
    {
      id: 'booking-forms',
      title: 'Booking Forms',
      subtitle: 'Manage booking forms',
      icon: <DocumentTextIcon className="w-5 h-5" />,
      action: () => navigate('/booking-forms'),
      keywords: ['forms', 'templates'],
      category: 'navigation',
    },
    {
      id: 'notifications',
      title: 'Notifications',
      subtitle: 'View notifications',
      icon: <BellIcon className="w-5 h-5" />,
      action: () => navigate('/notifications'),
      keywords: ['alerts', 'messages'],
      category: 'navigation',
    },
    {
      id: 'settings',
      title: 'Settings',
      subtitle: 'App settings',
      icon: <Cog6ToothIcon className="w-5 h-5" />,
      action: () => navigate('/settings'),
      keywords: ['preferences', 'config', 'options'],
      category: 'navigation',
    },
    {
      id: 'profile',
      title: 'Profile',
      subtitle: 'Your profile',
      icon: <UserIcon className="w-5 h-5" />,
      action: () => navigate('/profile'),
      keywords: ['account', 'me', 'user'],
      category: 'navigation',
    },
    {
      id: 'billing',
      title: 'Billing',
      subtitle: 'Billing & subscription',
      icon: <CreditCardIcon className="w-5 h-5" />,
      action: () => navigate('/billing'),
      keywords: ['payment', 'invoice', 'subscription'],
      category: 'navigation',
    },
    {
      id: 'schedule-view',
      title: 'Schedule View',
      subtitle: 'Team schedule overview',
      icon: <ClockIcon className="w-5 h-5" />,
      action: () => navigate('/schedule-view'),
      keywords: ['timeline', 'team', 'availability'],
      category: 'navigation',
    },
  ];

  // Filter commands based on search
  const filteredCommands = commands.filter((cmd) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      cmd.title.toLowerCase().includes(searchLower) ||
      cmd.subtitle?.toLowerCase().includes(searchLower) ||
      cmd.keywords?.some((k) => k.includes(searchLower))
    );
  });

  // Handle keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K to open
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setSearch('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, filteredCommands.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && filteredCommands[selectedIndex]) {
        e.preventDefault();
        filteredCommands[selectedIndex].action();
        setIsOpen(false);
      }
    },
    [filteredCommands, selectedIndex]
  );

  // Reset selected index when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ink-950/60 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
      />

      {/* Modal */}
      <div className="relative w-full max-w-xl bg-surface rounded-2xl shadow-2xl shadow-ink-950/20 overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-ink-200">
          <MagnifyingGlassIcon className="w-5 h-5 text-ink-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="search"
            placeholder="Search or jump to..."
            className="flex-1 text-base text-ink-900 placeholder-ink-400 bg-transparent outline-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-ink-400 bg-ink-100 rounded">
            esc
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto py-2">
          {filteredCommands.length === 0 ? (
            <div className="px-4 py-8 text-center text-ink-500">
              No results found for "{search}"
            </div>
          ) : (
            <>
              {/* Navigation section */}
              <div className="px-3 py-2">
                <div className="text-xs font-medium text-ink-400 uppercase tracking-wider px-2 mb-1">
                  Navigation
                </div>
                {filteredCommands.map((cmd, index) => (
                  <button
                    key={cmd.id}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      index === selectedIndex
                        ? 'bg-blue-50 text-blue-900'
                        : 'text-ink-700 hover:bg-ink-100'
                    }`}
                    onClick={() => {
                      cmd.action();
                      setIsOpen(false);
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <span
                      className={`flex-shrink-0 ${
                        index === selectedIndex ? 'text-blue-500' : 'text-ink-400'
                      }`}
                    >
                      {cmd.icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{cmd.title}</div>
                      {cmd.subtitle && (
                        <div
                          className={`text-sm truncate ${
                            index === selectedIndex ? 'text-blue-600/70' : 'text-ink-500'
                          }`}
                        >
                          {cmd.subtitle}
                        </div>
                      )}
                    </div>
                    {index === selectedIndex && (
                      <ArrowRightIcon className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-ink-200 bg-ink-50 flex items-center justify-between text-xs text-ink-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-surface border border-ink-200 rounded text-ink-600">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-surface border border-ink-200 rounded text-ink-600">↓</kbd>
              <span className="ml-1">to navigate</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-surface border border-ink-200 rounded text-ink-600">↵</kbd>
              <span className="ml-1">to select</span>
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-surface border border-ink-200 rounded text-ink-600">esc</kbd>
            <span className="ml-1">to close</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
